import { unique } from "@redotech/util/array";
import { escapeRegExp } from "@redotech/util/string";
import * as z from "zod";

const brandAliasSchema_ = z.object({
  id: z.bigint(),
  alias: z.string(),
  regexMatch: z.string(),
});
export const BrandAliasSchema: z.ZodType<BrandAlias> = brandAliasSchema_;
export interface BrandAlias extends z.infer<typeof brandAliasSchema_> {}

export function countBrandMentionsWithAliases({
  textToScan,
  brandName,
  regexMatches,
}: {
  textToScan: string;
  brandName: string;
  regexMatches: string[];
}): { mentionCount: number; brandMatches: BrandMatch[] } {
  const matcher = buildBrandMentionsMatcher(brandName, regexMatches);
  const { count, matches } = matcher.match(textToScan);

  return { mentionCount: count, brandMatches: matches };
}

function safeRegex(pattern: string): string {
  try {
    new RegExp(pattern);
    return pattern;
  } catch {
    // If the whole pattern is invalid, try each pipe-separated alternative individually
    const alternatives = pattern.split(/(?<!\\)\|/);
    if (alternatives.length <= 1) {
      return escapeRegExp(pattern);
    }
    const safeAlternatives = alternatives.map((alt) => {
      try {
        new RegExp(alt);
        return alt;
      } catch {
        return escapeRegExp(alt);
      }
    });
    return safeAlternatives.join("|");
  }
}

function ensureWordBoundaries(pattern: string): string {
  const trimmed = pattern.trim();
  const start = trimmed.startsWith("\\b") ? trimmed : `\\b${trimmed}`;
  const end = start.endsWith("\\b") ? start : `${start}\\b`;
  return end;
}

interface BrandMatch {
  matchText: string;
  start: number;
  end: number;
}

function buildBrandMentionsMatcher(brandName: string, regexPatterns: string[]) {
  const patterns: string[] = [];

  const safeRegexPatterns = unique(
    [escapeRegExp(brandName), ...regexPatterns.map(safeRegex)].filter(
      (p) => p.trim().length > 0,
    ),
  );
  for (const pattern of safeRegexPatterns) {
    patterns.push(ensureWordBoundaries(pattern));
  }
  const uniquePatterns = unique(patterns);
  uniquePatterns.sort((a, b) => b.length - a.length);

  const combined = new RegExp(uniquePatterns.join("|"), "g");

  return {
    match(text: string): { count: number; matches: BrandMatch[] } {
      combined.lastIndex = 0;
      const raw: BrandMatch[] = [];
      let m: RegExpExecArray | null;

      while ((m = combined.exec(text)) !== null) {
        raw.push({
          matchText: m[0],
          start: m.index,
          end: m.index + m[0].length,
        });
        if (m[0].length === 0) {
          combined.lastIndex++;
        }
      }

      raw.sort(
        (a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start,
      );
      const kept: BrandMatch[] = [];
      for (const c of raw) {
        if (!kept.some((k) => c.start < k.end && c.end > k.start)) {
          kept.push(c);
        }
      }
      kept.sort((a, b) => a.start - b.start);

      return { count: kept.length, matches: kept };
    },
  };
}
